export type MessageContent =
  | { type: "messageText"; messageText: string }
  | { type: "messageHtml"; messageHtml: string };

export interface PreparedEmailMessageContent {
  estimatedHeight: number;
  iframeContent: string;
}

const MIN_IFRAME_HEIGHT = 48;
const MAX_IFRAME_HEIGHT = 12_000;
const MAX_CACHED_IFRAME_HEIGHTS = 300;
const IFRAME_WIDTH_BUCKET_SIZE = 80;
const IFRAME_VERTICAL_PADDING = 32;
const ESTIMATED_CHARACTERS_PER_LINE = 90;
const ESTIMATED_LINE_HEIGHT = 21;
const ESTIMATED_IMAGE_HEIGHT = 180;

const iframeHeightCache = new Map<string, number>();

export function prepareEmailMessageContent(
  messageContent: MessageContent,
): PreparedEmailMessageContent {
  const processedMessage = processEmailMessageContent(messageContent);
  return {
    estimatedHeight: estimateEmailMessageIframeHeight(processedMessage),
    iframeContent: buildEmailMessageIframeContent(processedMessage),
  };
}

export function getEmailMessageHeightCacheKey(
  cacheKey: string | null | undefined,
  iframeWidth: number,
): string | null {
  if (!cacheKey) {
    return null;
  }

  const widthBucket =
    Math.round(iframeWidth / IFRAME_WIDTH_BUCKET_SIZE) *
    IFRAME_WIDTH_BUCKET_SIZE;
  return `${cacheKey}:w${widthBucket}`;
}

export function getCachedEmailMessageHeight(
  cacheKey: string | null | undefined,
): number | null {
  if (!cacheKey) {
    return null;
  }

  return iframeHeightCache.get(cacheKey) ?? null;
}

export function setCachedEmailMessageHeight(
  cacheKey: string | null | undefined,
  height: number,
): void {
  if (!cacheKey) {
    return;
  }

  iframeHeightCache.delete(cacheKey);
  iframeHeightCache.set(cacheKey, height);

  while (iframeHeightCache.size > MAX_CACHED_IFRAME_HEIGHTS) {
    const oldestKey = iframeHeightCache.keys().next().value;
    if (oldestKey === undefined) {
      return;
    }
    iframeHeightCache.delete(oldestKey);
  }
}

export function clampEmailMessageIframeHeight(height: number): number {
  if (!Number.isFinite(height) || height <= 0) {
    return MIN_IFRAME_HEIGHT;
  }

  return Math.max(
    MIN_IFRAME_HEIGHT,
    Math.min(MAX_IFRAME_HEIGHT, Math.ceil(height)),
  );
}

function processEmailMessageContent(messageContent: MessageContent): string {
  return messageContent.type === "messageHtml"
    ? processEmailMessageHtml(messageContent.messageHtml)
    : processPlainTextEmailMessage(messageContent.messageText);
}

function processPlainTextEmailMessage(messageText: string): string {
  let processedMessage = "";
  let lastIndex = 0;

  for (const match of messageText.matchAll(getImageUrlRegExp())) {
    const imageUrl = match[0];
    const matchIndex = match.index ?? 0;
    processedMessage += escapeHtml(messageText.slice(lastIndex, matchIndex));
    processedMessage += `<br /><img alt="" src="${escapeUrlAttribute(
      imageUrl,
    )}" />`;
    lastIndex = matchIndex + imageUrl.length;
  }

  processedMessage += escapeHtml(messageText.slice(lastIndex));
  return processedMessage.replace(/\n/g, "<br>").replace(/&nbsp;/g, " ");
}

function processEmailMessageHtml(messageHTML: string): string {
  return messageHTML
    .replace(
      getImageUrlRegExp(),
      (match) => `<br /><img alt="" src="${escapeUrlAttribute(match)}" />`,
    )
    .replace(/&nbsp;/g, " ");
}

function getImageUrlRegExp(): RegExp {
  return /(https?:\/\/[^\s<>"']+\.(?:png|jpg|gif|jpeg|webp)(?:\?[^\s<>"']*)?)(?![^<]*>)/gi;
}

function buildEmailMessageIframeContent(processedMessage: string): string {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <meta name="referrer" content="no-referrer" />
      <meta
        http-equiv="Content-Security-Policy"
        content="default-src 'none'; img-src https: data: blob:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'; navigate-to https: mailto: tel:"
      />
      <style>
        html {
          background: transparent;
          overflow: hidden;
        }

        body {
          margin: 0;
          padding: 16px;
          font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: var(--redo-colors-text-text-primary-900, #000);
          background: transparent;
          overflow-wrap: anywhere;
        }

        p {
          margin-block-start: 0;
          margin-block-end: 0;
        }

        img {
          display: block;
          height: auto;
          max-height: min(800px, 70vh);
          max-width: 100%;
          object-fit: contain;
        }

        pre {
          white-space: pre-wrap;
        }

        a {
          color: var(--redo-colors-semantic-link-500, #0066cc);
          text-decoration: underline;
        }

        a:hover {
          text-decoration: none;
        }
      </style>
    </head>
    <body>
      ${processedMessage}
    </body>
    </html>
  `;
}

function estimateEmailMessageIframeHeight(processedMessage: string): number {
  const structuralBreaks =
    processedMessage.match(/<br\b|<\/p>|<\/div>|<\/li>|<\/tr>/gi)?.length ?? 0;
  const imageCount = processedMessage.match(/<img\b/gi)?.length ?? 0;
  const textLength = processedMessage
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim().length;
  const lineCount = Math.max(
    1,
    Math.ceil(textLength / ESTIMATED_CHARACTERS_PER_LINE) + structuralBreaks,
  );

  return clampEmailMessageIframeHeight(
    IFRAME_VERTICAL_PADDING +
      lineCount * ESTIMATED_LINE_HEIGHT +
      imageCount * ESTIMATED_IMAGE_HEIGHT,
  );
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return character;
    }
  });
}

function escapeHtmlAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, "&#96;");
}

function escapeUrlAttribute(value: string): string {
  return escapeHtmlAttribute(value.replace(/&amp;/gi, "&"));
}
